<template>
  <v-app>
    <!-- ================= SEARCH PANEL ================= -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="From Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-date-input
                        label="To Date"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar-month"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col lg="2" md="2" cols="12">
                      <v-btn class="bg-brand w-15" type="submit">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= RECORD LIST ================= -->
    <v-container fluid style="margin-top: -20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Purchase Order Records
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    />
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-btn
                        @click="goToCreate"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-plus"
                        rounded="xl"
                      >
                        Add Purchase Order
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <div class="scrollable-table-wrapper">
                      <v-data-table
                        :headers="headers"
                        :items="purchaseOrderList"
                        :loading="loading"
                        :search="search"
                        density="compact"
                        
                      >
                      <!-- show-expand -->
                        <template #item.index="{ index }">
                          {{ index + 1 }}
                        </template>

                        <template #item.purchase_order_no="{ item }">
                          {{ item.purchase_order_no }}
                        </template>

                        <template #item.buyer="{ item }">
                          {{ item.buyer?.customer?.company_name || "-" }}
                        </template>

                        <template #item.invoice_date="{ item }">
                          {{ formatDate(item.invoice_date) }}
                        </template>

                        <template #item.delivery_date="{ item }">
                          {{ formatDate(item.delivery_date) }}
                        </template>

                        <template #item.total_items="{ item }">
                          {{ item.items ? item.items.length : 0 }}
                        </template>

                        <template #item.status="{ item }">
                          <v-chip
                            size="small"
                            :color="item.status == 1 ? 'success' : 'grey'"
                            variant="tonal"
                          >
                            {{ item.status == 1 ? "Final" : "Draft" }}
                          </v-chip>
                        </template>

                        <template #item.actions="{ item }">
                          <v-btn
                            icon="mdi-pencil"
                            size="small"
                            variant="text"
                            color="primary"
                            @click="goToEdit(item.id)"
                          />

                          <v-btn
                            v-bind="props"
                            icon="mdi-file"
                            size="small"
                            variant="text"
                            color="info"
                            @click="viewDetails(item.id)"
                          ></v-btn>
                        </template>


                        <template #no-data>
                          <div class="text-center py-4">
                            No purchase order records found
                          </div>
                        </template>
                      </v-data-table>
                    </div>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

/* ---------------- BASIC ---------------- */
const store = useStore();
const router = useRouter();

const panel = ref([0]);
const loading = ref(false);
const search = ref("");

/* ---------------- FORM ---------------- */
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

const submit = handleSubmit(() => {
  loadList();
});

/* ---------------- HEADERS ---------------- */
const headers = [
  { title: "#SL", key: "index" },
  { title: "PO No", key: "purchase_order_no" },
  { title: "Buyer", key: "buyer.name" },
  { title: "Invoice Date", key: "invoice_date" },
  { title: "Shipment Date", key: "shipment_date" },
  // { title: "Items", key: "total_items" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions" },
];

const itemHeaders = [
  { title: "Product", key: "product" },
  { title: "Qty", key: "quantity" },
  { title: "Rate", key: "rate" },
  { title: "Amount", key: "amount" },
];

/* ---------------- DATA ---------------- */
const purchaseOrderList = computed(() => {
  return store.getters.getBhpPurchaseOrderList || [];
});

/* ---------------- METHODS ---------------- */
function loadList(){
  loading.value = true;
  store
    .dispatch("getBhpPurchaseOrderList", {
      from_date: from_date.value.value,
      to_date: to_date.value.value,
    })
    .finally(() => {
      loading.value = false;
    });
}

function formatDate(date){
  if (!date) return "-";
  return new Date(date).toLocaleDateString();
};

function goToCreate() {
  router.push({ name: "purchase-order-create" });
};

function goToEdit(id){
  router.push({ name: "purchase-order-create", params: { id } });
};

function viewDetails(id){
  router.push({ name: "purchase-order-view", params: { id } });
}

const exportToCSV = () => toast.info("CSV export coming soon");
const exportToExcel = () => toast.info("Excel export coming soon");
const exportToPDF = () => toast.info("PDF export coming soon");

/* ---------------- INIT ---------------- */
onMounted(loadList);
</script>

<style scoped>
.scrollable-table-wrapper {
  overflow-x: auto;
}
</style>
